import React, { useEffect, useState } from 'react';
import { fetchCompanyInfo } from '../services/companyInfo';
import {
  Box, Container, Paper, Typography, TextField, Button,
  Tab, Tabs, Alert, InputAdornment, IconButton, Divider, Grid,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { Visibility, VisibilityOff, LocalShipping, Person, Business, Search, PhoneAndroid, MenuBook } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { customerAuthAPI } from '../services/api';

const DRIVER_APP_URL = 'https://nara-logistics.kr/downloads/nara-driver.apk';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function HomePage({ onLogin }) {
  const [tab, setTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerType, setCustomerType] = useState('corporate');

  const [company, setCompany] = useState({ company_name: '' });
  useEffect(() => { fetchCompanyInfo().then(setCompany).catch(() => {}); }, []);

  const [loginForm, setLoginForm] = useState({ phone: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    name: '', phone: '', password: '', password_confirm: '',
    company_name: '', business_number: '', address: '', ceo_name: '',
    email: '', office_phone: '', start_dt: '',
  });
  // 이메일 인증 상태
  const [emailOtp, setEmailOtp] = useState({ code: '', sent: false, verified: false, sending: false, verifying: false });
  // 진위확인 필요 여부 (사업자번호 재가입 시 업체명 불일치)
  const [needIdentity, setNeedIdentity] = useState(false);

  // 사업자번호 000-00-00000 자동 하이픈
  const formatBizNo = (value) => {
    const d = (value || '').replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5,10)}`;
  };
  // 개업일자 YYYY-MM-DD 포맷 (입력은 type=date 추천)

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 8 && !digits.startsWith('0')) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    if (digits.startsWith('02')) {
      if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
      if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
    }
    if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    return digits.slice(0, 11);
  };

  const openAddressSearch = () => {
    if (!window.daum || !window.daum.Postcode) {
      alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    new window.daum.Postcode({
      oncomplete: (data) => {
        const addr = data.roadAddress || data.jibunAddress;
        setSignupForm((prev) => ({ ...prev, address: addr }));
      },
    }).open();
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginForm({ ...loginForm, [name]: name === 'phone' ? formatPhone(value) : value });
    setError('');
  };

  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    if (name === 'phone') v = formatPhone(value);
    else if (name === 'business_number') v = formatBizNo(value);
    // 이메일 변경 시 인증 상태 reset
    if (name === 'email') setEmailOtp({ code: '', sent: false, verified: false, sending: false, verifying: false });
    setSignupForm({ ...signupForm, [name]: v });
    setError('');
  };

  const handleSendOtp = async () => {
    if (!signupForm.email) return setError('이메일을 먼저 입력하세요.');
    setEmailOtp((p) => ({ ...p, sending: true }));
    try {
      await customerAuthAPI.sendEmailOtp(signupForm.email);
      setEmailOtp((p) => ({ ...p, sent: true, sending: false }));
      alert('인증 코드를 이메일로 전송했습니다. 10분 안에 입력해 주세요.');
    } catch (err) {
      setEmailOtp((p) => ({ ...p, sending: false }));
      setError(err.response?.data?.message || '인증 코드 전송 실패');
    }
  };
  const handleVerifyOtp = async () => {
    if (!emailOtp.code) return setError('인증 코드를 입력하세요.');
    setEmailOtp((p) => ({ ...p, verifying: true }));
    try {
      await customerAuthAPI.verifyEmailOtp(signupForm.email, emailOtp.code);
      setEmailOtp((p) => ({ ...p, verified: true, verifying: false }));
    } catch (err) {
      setEmailOtp((p) => ({ ...p, verifying: false }));
      setError(err.response?.data?.message || '인증 실패');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await customerAuthAPI.login({ phone: loginForm.phone, password: loginForm.password });
      onLogin(res.data.token, res.data.user);
    } catch (err) {
      setError(err.response?.data?.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!signupForm.name) return setError('이름을 입력하세요.');
    if (!signupForm.phone) return setError('휴대폰 번호를 입력하세요.');
    if (!signupForm.password) return setError('비밀번호를 입력하세요.');
    if (signupForm.password.length < 6) return setError('비밀번호는 6자 이상이어야 합니다.');
    if (signupForm.password !== signupForm.password_confirm) return setError('비밀번호가 일치하지 않습니다.');
    if (customerType === 'corporate' && !signupForm.company_name) return setError('업체명을 입력하세요.');

    // 이메일 인증 필수
    if (!signupForm.email) return setError('이메일을 입력하고 인증해 주세요.');
    if (!emailOtp.verified) return setError('이메일 인증을 완료해 주세요.');

    // 기업 회원: 사업자번호 + 대표자명 + 개업일자 필수 + 체크섬 검증
    if (customerType === 'corporate') {
      const bn = (signupForm.business_number || '').replace(/\D/g, '');
      if (bn.length !== 10) return setError('사업자등록번호 10자리를 입력하세요.');
      const weights = [1, 3, 7, 1, 3, 7, 1, 3, 5];
      let sum = 0;
      for (let i = 0; i < 9; i++) sum += Number(bn[i]) * weights[i];
      sum += Math.floor((Number(bn[8]) * 5) / 10);
      const checkDigit = (10 - (sum % 10)) % 10;
      if (checkDigit !== Number(bn[9])) {
        return setError('사업자등록번호 형식이 올바르지 않습니다. (체크섬 불일치)');
      }
      if (!signupForm.ceo_name || !signupForm.ceo_name.trim()) {
        return setError('대표자명을 입력하세요. (국세청 진위확인용)');
      }
      if (!signupForm.start_dt || signupForm.start_dt.replace(/\D/g, '').length !== 8) {
        return setError('개업일자(YYYY-MM-DD)를 입력하세요. (국세청 진위확인용)');
      }
    }

    setLoading(true);
    try {
      const { password_confirm, ...rest } = signupForm;
      const submitData = { ...rest, customer_type: customerType };
      if (customerType === 'personal') {
        submitData.company_name = '';
        submitData.business_number = '';
        submitData.address = '';
        submitData.ceo_name = '';
        submitData.email = '';
        submitData.office_phone = '';
      } else {
        submitData.office_phone = formatPhone(submitData.office_phone || '');
      }
      const res = await customerAuthAPI.register(submitData);
      // 신규 가입은 직원 승인 대기 상태로 들어감 → 자동 로그인 X, 안내 메시지 + 로그인 탭 전환
      if (res.data?.pending_approval) {
        alert(res.data?.message || '회원가입 신청이 접수되었습니다. 담당 직원의 승인 후 로그인할 수 있습니다.');
        setTab(0);
      } else if (res.data?.token) {
        onLogin(res.data.token, res.data.user);
      } else {
        alert('회원가입이 완료되었습니다. 로그인해 주세요.');
        setTab(0);
      }
    } catch (err) {
      const msg = err.response?.data?.message || '회원가입에 실패했습니다.';
      // 진위확인 폴백 요구 — 사용자에게 대표자명/개업일자 안내
      if (err.response?.data?.require_identity) setNeedIdentity(true);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 50%, #01579b 100%)' }}>
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <LocalShipping sx={{ fontSize: 48, color: 'white', mb: 1 }} />
        <Typography variant="h3" fontWeight={700} color="white" gutterBottom>{company.company_name || ''}</Typography>
        <Typography variant="h6" color="rgba(255,255,255,0.85)">빠르고 안전한 화물 배송 서비스</Typography>
      </Box>

      <Container maxWidth="sm" sx={{ pb: 6 }}>
        <Paper elevation={8} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setError(''); setSuccess(''); }}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}
          >
            <Tab label="로그인" sx={{ fontWeight: 600, fontSize: '1rem', py: 2 }} />
            <Tab label="회원가입" sx={{ fontWeight: 600, fontSize: '1rem', py: 2 }} />
          </Tabs>

          <Box sx={{ p: 4 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* 로그인 */}
            <TabPanel value={tab} index={0}>
              <form onSubmit={handleLogin}>
                <TextField
                  fullWidth label="연락처 (휴대폰 번호)" name="phone"
                  value={loginForm.phone} onChange={handleLoginChange}
                  placeholder="010-1234-5678" sx={{ mb: 2 }} required
                />
                <TextField
                  fullWidth label="비밀번호" name="password" type={showPassword ? 'text' : 'password'}
                  value={loginForm.password} onChange={handleLoginChange}
                  sx={{ mb: 3 }} required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={loading}
                  sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 2 }}
                >
                  {loading ? '로그인 중...' : '로그인'}
                </Button>
              </form>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  아직 계정이 없으신가요?{' '}
                  <Button size="small" onClick={() => setTab(1)} sx={{ fontWeight: 700 }}>회원가입</Button>
                </Typography>
              </Box>
            </TabPanel>

            {/* 회원가입 */}
            <TabPanel value={tab} index={1}>
              {/* 개인 / 기업 선택 */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <ToggleButtonGroup
                  value={customerType}
                  exclusive
                  onChange={(_, v) => { if (v) { setCustomerType(v); setError(''); } }}
                  sx={{ width: '100%' }}
                >
                  <ToggleButton value="corporate" sx={{ flex: 1, py: 1.5, fontWeight: 600, fontSize: '0.95rem' }}>
                    <Business sx={{ mr: 1 }} /> 기업 고객
                  </ToggleButton>
                  <ToggleButton value="personal" sx={{ flex: 1, py: 1.5, fontWeight: 600, fontSize: '0.95rem' }}>
                    <Person sx={{ mr: 1 }} /> 개인
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <form onSubmit={handleSignup} noValidate>
                <Grid container spacing={2}>
                  {/* 공통 필드 */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="이름 *" name="name"
                      value={signupForm.name} onChange={handleSignupChange}
                      placeholder="홍길동"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="휴대폰 번호 * (아이디)" name="phone"
                      value={signupForm.phone} onChange={handleSignupChange}
                      placeholder="010-1234-5678"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="비밀번호 *" name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={signupForm.password} onChange={handleSignupChange}
                      helperText="6자 이상"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="비밀번호 확인 *" name="password_confirm"
                      type="password" value={signupForm.password_confirm}
                      onChange={handleSignupChange}
                    />
                  </Grid>

                  {/* 기업 전용 필드 */}
                  {customerType === 'corporate' && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">기업 정보</Typography>
                        </Divider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="업체명 *" name="company_name"
                          value={signupForm.company_name} onChange={handleSignupChange}
                          placeholder="(주)나라상사"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="대표자명" name="ceo_name"
                          value={signupForm.ceo_name} onChange={handleSignupChange}
                          placeholder="김대표"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="사업자번호 *" name="business_number"
                          value={signupForm.business_number} onChange={handleSignupChange}
                          placeholder="000-00-00000"
                          inputProps={{ maxLength: 12 }}
                          helperText="국세청에서 진위확인합니다"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="개업일자 *" name="start_dt" type="date"
                          value={signupForm.start_dt} onChange={handleSignupChange}
                          InputLabelProps={{ shrink: true }}
                          helperText="사업자등록증의 개업연월일을 입력하세요"
                        />
                      </Grid>
                      <Grid item xs={12} sm={emailOtp.verified ? 6 : 12}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <TextField
                            fullWidth size="medium" label={emailOtp.verified ? "E-Mail (인증 완료)" : "E-Mail"} name="email" type="email"
                            value={signupForm.email} onChange={handleSignupChange}
                            placeholder="예: contact@company.co.kr"
                            disabled={emailOtp.verified}
                          />
                          {!emailOtp.verified && (
                            <Button variant="outlined" sx={{ whiteSpace: 'nowrap', height: 56 }} disabled={emailOtp.sending || !signupForm.email} onClick={handleSendOtp}>
                              {emailOtp.sending ? '전송중' : (emailOtp.sent ? '재전송' : '인증코드')}
                            </Button>
                          )}
                        </Box>
                        {emailOtp.sent && !emailOtp.verified && (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <TextField
                              fullWidth size="small" label="인증 코드 (6자리)"
                              value={emailOtp.code}
                              onChange={(e) => setEmailOtp((p) => ({ ...p, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                              inputProps={{ maxLength: 6 }}
                            />
                            <Button variant="contained" sx={{ whiteSpace: 'nowrap' }} disabled={emailOtp.verifying || !emailOtp.code} onClick={handleVerifyOtp}>
                              {emailOtp.verifying ? '확인중' : '확인'}
                            </Button>
                          </Box>
                        )}
                        {emailOtp.verified && (
                          <Box sx={{ color: 'success.main', fontSize: 13, mt: 0.5 }}>✓ 이메일 인증 완료</Box>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="사무실 연락처" name="office_phone"
                          value={signupForm.office_phone}
                          onChange={(e) => setSignupForm({ ...signupForm, office_phone: formatPhone(e.target.value) })}
                          placeholder="02-1234-5678"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth label="주소" name="address"
                          value={signupForm.address} onChange={handleSignupChange}
                          placeholder="주소 검색 버튼을 눌러 입력하세요"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button size="small" variant="outlined" startIcon={<Search />}
                                  onClick={openAddressSearch} sx={{ whiteSpace: 'nowrap' }}>
                                  검색
                                </Button>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Button
                      type="submit" fullWidth variant="contained" size="large"
                      disabled={loading}
                      sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: 2, mt: 1 }}
                    >
                      {loading ? '처리 중...' : '회원가입'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </TabPanel>
          </Box>
        </Paper>

        <Paper
          elevation={4}
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #e3f2fd 100%)',
            textAlign: 'center',
          }}
        >
          <PhoneAndroid sx={{ fontSize: 40, color: '#1565c0', mb: 1 }} />
          <Typography variant="h6" fontWeight={700} color="#1565c0" gutterBottom>
            운전기사용 앱 설치
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            QR코드를 스캔하거나 버튼을 눌러 앱을 설치하세요
          </Typography>

          <Box sx={{
            display: 'inline-block',
            p: 2,
            borderRadius: 3,
            bgcolor: 'white',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            mb: 2.5,
          }}>
            <QRCodeSVG
              value={DRIVER_APP_URL}
              size={180}
              level="H"
              includeMargin={false}
              fgColor="#1565c0"
            />
          </Box>

          <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
            휴대폰 카메라로 QR코드를 스캔하세요
          </Typography>
          <Typography
            variant="caption"
            display="block"
            component="a"
            href={DRIVER_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mb: 2,
              color: '#1565c0',
              textDecoration: 'underline',
              wordBreak: 'break-all',
              cursor: 'pointer',
              '&:hover': { color: '#0d47a1' },
            }}
          >
            {DRIVER_APP_URL}
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<PhoneAndroid />}
            href={DRIVER_APP_URL}
            target="_blank"
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1rem',
              fontWeight: 700,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)' },
            }}
          >
            Android 앱 다운로드
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 1.5, color: 'text.secondary' }}>
            * Android 기기에서 다운로드 후 설치해 주세요
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Button
            variant="outlined"
            size="medium"
            startIcon={<MenuBook />}
            href="/install-guide"
            sx={{
              fontWeight: 600,
              borderRadius: 2,
              borderColor: '#1565c0',
              color: '#1565c0',
              '&:hover': { bgcolor: '#e3f2fd', borderColor: '#0d47a1' },
            }}
          >
            설치 방법 자세히 보기
          </Button>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.3 }}>
            상호: {company.company_name || ''}
            {company.ceo_name ? ` | 대표: ${company.ceo_name}` : ''}
            {company.business_number ? ` | 사업자번호: ${company.business_number}` : ''}
          </Typography>
          {company.company_address && (
            <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.3 }}>
              주소: {company.company_address}
            </Typography>
          )}
          {company.company_phone && (
            <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              고객센터: {company.company_phone}
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
}
